import { useState, useRef, useCallback, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';

type Msg = { role: 'user' | 'assistant'; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;

const QUICK_PROMPTS = [
  'What services do you offer?',
  'How much does it cost?',
  'Book a discovery call',
];

const LEAD_TRIGGERS = [
  'name', 'email', 'phone', 'contact', 'reach', 'call me', 'get in touch',
  'interested', 'sign me up', 'book', 'schedule', 'quote', 'pricing',
  'how much', 'cost', 'ready to start', 'let\'s go', 'info',
];

const ASSISTANT_LEAD_TRIGGERS = [
  'your name', 'your email', 'your phone', 'contact info',
  'reach out', 'get back to you', 'share your', 'provide your',
  'schedule your', 'book your', 'so our team can',
];

const FloatingChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { role: 'assistant', content: "Hey! 👋 I'm VPM's virtual assistant. Ask me anything about our video production, social media, or content strategy services — or I can help you book a free discovery call!" },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadForm, setLeadForm] = useState({ full_name: '', email: '', phone: '' });
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, showLeadForm]);

  // Check if we should show the lead form based on conversation context
  const shouldShowLeadForm = useCallback((allMessages: Msg[], latestAssistant?: string) => {
    if (leadSubmitted || showLeadForm) return false;
    // If the assistant is asking for contact info, show form immediately
    if (latestAssistant) {
      const lower = latestAssistant.toLowerCase();
      if (ASSISTANT_LEAD_TRIGGERS.some(t => lower.includes(t))) return true;
    }
    // Show after 3+ user exchanges or if user mentions trigger words
    const userMessages = allMessages.filter(m => m.role === 'user');
    if (userMessages.length >= 3) return true;
    const lastUser = userMessages[userMessages.length - 1]?.content.toLowerCase() || '';
    return LEAD_TRIGGERS.some(t => lastUser.includes(t));
  }, [leadSubmitted, showLeadForm]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;
    const userMsg: Msg = { role: 'user', content: text.trim() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setIsLoading(true);
    messageCountRef.current++;

    let assistantSoFar = '';

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: history }),
      });

      if (!resp.ok || !resp.body) throw new Error('Stream failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6).trim();
          if (json === '[DONE]') break;
          try {
            const parsed = JSON.parse(json);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantSoFar += content;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant' && prev.length > history.length) {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantSoFar } : m);
                }
                return [...prev, { role: 'assistant', content: assistantSoFar }];
              });
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // After response, check if we should surface the lead form
      if (shouldShowLeadForm(history, assistantSoFar)) {
        setTimeout(() => setShowLeadForm(true), 400);
      }
    } catch (e) {
      console.error(e);
      setMessages((prev) => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble connecting right now. You can always reach us at our booking page!" }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, shouldShowLeadForm]);

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.full_name.trim() || !leadForm.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setLeadLoading(true);
    try {
      // Build context from chat history
      const chatSummary = messages
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join(' | ');

      const { error } = await supabase.from('leads').insert({
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        service_interest: 'Chatbot Inquiry',
        additional_info: `Chat context: ${chatSummary.slice(0, 900)}`,
      });
      if (error) throw error;

      await supabase.functions.invoke('send-lead-notification', {
        body: {
          full_name: leadForm.full_name,
          email: leadForm.email,
          phone: leadForm.phone,
          service_interest: 'Chatbot Inquiry',
          additional_info: `Chat context: ${chatSummary.slice(0, 900)}`,
        },
      });

      setLeadSubmitted(true);
      setShowLeadForm(false);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Thanks, ${leadForm.full_name}! 🎉 Our team will review your inquiry and reach out shortly. In the meantime, feel free to keep asking questions!`,
      }]);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLeadLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 md:bottom-6 right-4 z-50 w-14 h-14 rounded-full bg-accent text-accent-foreground shadow-glow flex items-center justify-center hover:scale-110 transition-transform duration-200"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 md:bottom-6 right-4 z-50 w-[340px] sm:w-[380px] h-[500px] bg-background border border-border rounded-2xl shadow-strong flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" />
              <span className="font-display font-bold text-sm text-foreground">VPM Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-muted text-foreground rounded-bl-md'
                )}>
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none [&>p]:m-0">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : m.content}
                </div>
              </div>
            ))}

            {/* Inline Lead Capture Form */}
            {showLeadForm && !leadSubmitted && (
              <div className="flex justify-start animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="w-full max-w-[92%] rounded-2xl rounded-bl-md border border-accent/20 bg-accent/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-accent" />
                    <span className="text-sm font-semibold text-foreground">Let's connect you with the team</span>
                  </div>
                  <form onSubmit={handleLeadSubmit} className="space-y-2.5">
                    <div className="space-y-1">
                      <Label htmlFor="chat_name" className="text-xs text-muted-foreground">Name *</Label>
                      <Input
                        id="chat_name"
                        required
                        maxLength={100}
                        placeholder="Your full name"
                        className="h-8 text-sm bg-background border-border"
                        value={leadForm.full_name}
                        onChange={(e) => setLeadForm(p => ({ ...p, full_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="chat_email" className="text-xs text-muted-foreground">Email *</Label>
                      <Input
                        id="chat_email"
                        type="email"
                        required
                        maxLength={255}
                        placeholder="you@company.com"
                        className="h-8 text-sm bg-background border-border"
                        value={leadForm.email}
                        onChange={(e) => setLeadForm(p => ({ ...p, email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="chat_phone" className="text-xs text-muted-foreground">Phone <span className="text-muted-foreground/60">(optional)</span></Label>
                      <Input
                        id="chat_phone"
                        type="tel"
                        maxLength={20}
                        placeholder="(555) 123-4567"
                        className="h-8 text-sm bg-background border-border"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm(p => ({ ...p, phone: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button type="submit" variant="accent" size="sm" className="flex-1 h-8 text-xs" disabled={leadLoading}>
                        {leadLoading ? 'Sending…' : 'Submit'}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={() => setShowLeadForm(false)}>
                        Later
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-xs bg-accent/10 text-accent border border-accent/20 rounded-full px-3 py-1.5 hover:bg-accent/20 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="flex items-center gap-2 px-3 py-3 border-t border-border">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message…"
              className="flex-1 h-9 text-sm bg-card border-border rounded-full px-4"
              disabled={isLoading}
            />
            <Button type="submit" size="icon" variant="accent" className="h-9 w-9 rounded-full shrink-0" disabled={isLoading || !input.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default FloatingChatbot;
